import { useState } from "react";
import { Shield, ShieldCheck, Lock, Unlock } from "lucide-react";
import { toast } from "sonner";
import { useSuperMode } from "../../context/SuperModeContext";
import SuperModePinModal from "./SuperModePinModal";

/**
 * SuperModeToggle — small button placed in the admin top bar (beside Logout).
 * Only rendered for the seeded super admin. Hidden completely for regular admins.
 */
export default function SuperModeToggle() {
    const sm = useSuperMode();
    const [open, setOpen] = useState(false);

    if (sm.loading || !sm.isSuperAdmin) return null;

    const onClick = async () => {
        if (sm.active) {
            await sm.lock("manual");
            toast.success("Switched to Normal Mode", { description: "Privacy Mode is now active" });
            return;
        }
        setOpen(true);
    };

    const Icon = sm.active ? Unlock : Lock;
    const ActiveIcon = sm.active ? ShieldCheck : Shield;
    return (
        <>
            <button
                type="button"
                onClick={onClick}
                data-testid="super-mode-toggle-btn"
                className={`group inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-all shadow-sm border ${
                    sm.active
                        ? "bg-gradient-to-r from-fuchsia-600 to-violet-600 text-white border-violet-700 hover:from-fuchsia-700 hover:to-violet-700"
                        : "bg-slate-100 text-slate-700 border-slate-200 hover:bg-slate-200 hover:border-slate-300"
                }`}
                title={sm.active ? "You are in Super Admin Mode. Click to lock." : "Unlock Super Admin Mode"}
            >
                <ActiveIcon className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">
                    {sm.active ? "Switch to Normal Mode" : "Switch to Super Mode"}
                </span>
                <span className="sm:hidden">{sm.active ? "Normal" : "Super"}</span>
                <Icon className="w-3 h-3 opacity-70" />
            </button>
            <SuperModePinModal open={open} onClose={() => setOpen(false)} />
        </>
    );
}

/**
 * SuperModeBadge — small status pill rendered under the admin title bar.
 * "Privacy Mode Enabled" in normal mode, "Super Admin Mode Active" in super mode.
 */
export function SuperModeBadge() {
    const sm = useSuperMode();
    if (sm.loading || !sm.isSuperAdmin) return null;
    return (
        <div
            data-testid={sm.active ? "super-mode-badge-active" : "super-mode-badge-normal"}
            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider border transition-all animate-in fade-in duration-300 ${
                sm.active
                    ? "bg-gradient-to-r from-fuchsia-50 to-violet-50 border-violet-300 text-violet-800"
                    : "bg-emerald-50 border-emerald-200 text-emerald-700"
            }`}
        >
            <span className={`w-1.5 h-1.5 rounded-full ${sm.active ? "bg-violet-500 animate-pulse" : "bg-emerald-500"}`} />
            <span className="hidden sm:inline">{sm.active ? "Super Admin Mode Active" : "Privacy Mode Enabled"}</span>
            <span className="sm:hidden">{sm.active ? "Super" : "Privacy"}</span>
        </div>
    );
}
